/* Give-back calculation for the cart drawer.
 *
 * The cart drawer renders line items client-side from /cart.js, which does NOT
 * expose product tags or metafields. The old code therefore showed 10% of the
 * WHOLE cart as "going to charity" — wrong: only products designated to a
 * charity give back, and only their share does.
 *
 * This module mirrors the per-product rule used on the product page
 * (sections/main-an-product.liquid): a product gives back when one of its tags
 * (length > 4) is a substring of a charity's name; the share is that charity's
 * share_pct_default (null -> 0.10). Charity list + shares are emitted into
 * window.AN_CHARITIES by snippets/an-cart-drawer.liquid, in metaobject order so
 * "first matching charity wins" matches the product page exactly.
 *
 * Kept in its own file so the math is unit-testable in Node
 * (04-technical/scripts/tests/an-cart-give.test.cjs).
 */
(function (root) {
  // tags: string[]  charities: [{name, share}]  -> share (0 if not a cause product)
  function shareFromTags(tags, charities) {
    if (!tags || !tags.length || !charities) return 0;
    for (var i = 0; i < charities.length; i++) {
      var name = charities[i].name || '';
      for (var j = 0; j < tags.length; j++) {
        var t = tags[j];
        if (t && t.length > 4 && name.indexOf(t) !== -1) return charities[i].share || 0;
      }
    }
    return 0;
  }

  // For a product KNOWN to be a cause product (it is in the curated
  // adams-nest-gives-back collection), return its share: the matched charity's
  // share if a tag identifies one (e.g. 5% for Helping Our Women), else the
  // standard 10%. This is why a correctly-designated item never shows $0 even
  // when its tags don't cleanly name-match a charity.
  function shareForCauseProduct(tags, charities) {
    var s = shareFromTags(tags, charities);
    return s > 0 ? s : 0.1;
  }

  // Per-product override tags, mirroring sections/main-an-product.liquid:
  //   give-pct-15  -> 15% of price (Annie's Indivisible graphics)
  //   give-flat-10 -> flat $10 per item (AIDS Memorial apparel)
  // Returns {pct, flatCents}; zeros when no valid override tag is present.
  function overridesFromTags(tags) {
    var out = { pct: 0, flatCents: 0 };
    if (!tags || !tags.length) return out;
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i] || '');
      var m = /^give-pct-(\d+)$/.exec(t);
      if (m) { out.pct = parseInt(m[1], 10) / 100; continue; }
      m = /^give-flat-(\d+)$/.exec(t);
      if (m) out.flatCents = parseInt(m[1], 10) * 100;
    }
    return out;
  }

  // Full resolution for a curated cause product -> {share, flatCents}.
  // Flat beats percent; a percent override beats the charity default.
  function giveForCauseProduct(tags, charities) {
    var o = overridesFromTags(tags);
    if (o.flatCents > 0) return { share: 0, flatCents: o.flatCents };
    if (o.pct > 0) return { share: o.pct, flatCents: 0 };
    return { share: shareForCauseProduct(tags, charities), flatCents: 0 };
  }

  // lines: [{lineCents, share, flatCents?, qty?}] -> total give-back in cents.
  // Percent lines round per line, matching the product page's per-item
  // rounding; flat lines contribute flatCents x qty regardless of price.
  function giveCents(lines) {
    var total = 0;
    for (var i = 0; i < lines.length; i++) {
      var ln = lines[i];
      if (ln.flatCents > 0) total += ln.flatCents * (ln.qty || 1);
      else total += Math.round((ln.lineCents || 0) * (ln.share || 0));
    }
    return total;
  }

  // Adam's own routine decides what gives back (2026-10-16). A cart line is a
  // cause item when ANY of these is true:
  //   - it has the "give back" tag (any case)
  //   - its charity_beneficiary field names a charity
  //   - it is in the adams-nest-gives-back collection (the older, hand-kept list)
  //   - a tag matches a charity name the PDP way (substring, >4 chars), or
  //     equals a charity name in another case ("summer of sass", "Colage")
  // Rate: give-flat tag > give-pct tag > the product's charity_share_pct field
  //   > the matched charity's share > 10%.
  // info: {tags, beneficiary (charity name|null), sharePct (number|null), inGivesBack}
  function charityShare(name, charities) {
    for (var i = 0; i < (charities || []).length; i++) {
      if (charities[i].name === name) return charities[i].share || 0.1;
    }
    return 0;
  }

  function tagEqualsCharityShare(tags, charities) {
    for (var i = 0; i < (charities || []).length; i++) {
      var name = String(charities[i].name || '').toLowerCase();
      for (var j = 0; j < tags.length; j++) {
        if (String(tags[j] || '').toLowerCase() === name) return charities[i].share || 0.1;
      }
    }
    return 0;
  }

  function resolveLine(info, charities) {
    var none = { share: 0, flatCents: 0 };
    if (!info) return none;
    var tags = info.tags || [];
    var giveTag = tags.some(function (t) { return String(t || '').toLowerCase() === 'give back'; });
    var benShare = info.beneficiary ? charityShare(info.beneficiary, charities) : 0;
    var tagShare = shareFromTags(tags, charities) || tagEqualsCharityShare(tags, charities);
    if (!giveTag && !info.beneficiary && !info.inGivesBack && !tagShare) return none;

    var o = overridesFromTags(tags);
    if (o.flatCents > 0) return { share: 0, flatCents: o.flatCents };
    if (o.pct > 0) return { share: o.pct, flatCents: 0 };
    var own = parseFloat(info.sharePct);
    if (own > 0) return { share: own, flatCents: 0 };
    return { share: benShare || tagShare || 0.1, flatCents: 0 };
  }

  var api = {
    resolveLine: resolveLine,
    shareFromTags: shareFromTags,
    shareForCauseProduct: shareForCauseProduct,
    overridesFromTags: overridesFromTags,
    giveForCauseProduct: giveForCauseProduct,
    giveCents: giveCents
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.ANCartGive = api;
})(typeof window !== 'undefined' ? window : this);
